<template>
  <div>
    <LoadingToast
      v-model="isActivatorLoading"
      title="author.publishInActivator.opening"
      subtitle="author.publishInActivator.mayTakeAWhile"
      :progress="activatorProgress"
    />
    <LoadingToast
      v-model="isActivatorError"
      title="author.publishInActivator.wentWrong"
      subtitle="author.publishInActivator.tryAgain"
      :progress="0"
      :on-close="() => (isActivatorError = false)"
    />

    <BbBtn v-bind="{ type: 'primary' }" class="dropdown-button" size="small" @click="publishInActivator">
      <BbIcon size="small" name="act" color="#F242FF" />
      {{ t('author.publishInActivator.title') }}
      <template #menu>
        <BbMenuItem :is-disabled="isDownloadInProgress" @click="$emit('download')">
          <BbIcon size="small" :name="isDownloadInProgress ? 'spin' : 'download'" />
          <TextBody i18n-key="common.download" tag="span" />
        </BbMenuItem>
      </template>
    </BbBtn>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { IsDownloadInProgress } from '~/constants/getters';

import TextBody from '~/components/common/TextBody.vue';
import UndoRedo from '~/components/conversation/authoring/UndoRedo.vue';
import LoadingToast from '~/components/common/LoadingToast.vue';
import useAuthoring from '~/composables/useAuthoring';

export default {
  components: { TextBody, UndoRedo, LoadingToast },
  emits: ['download'],
  setup() {
    const route = useRoute();
    const store = useStore();
    const { t } = useI18n();
    const { getActivatorUrl } = useAuthoring();

    const isDownloadInProgress = computed(() =>
      store.getters[`documentStore/${IsDownloadInProgress}`](route.params.conversationId),
    );
    const isActivatorLoading = ref(false);
    const activatorProgress = ref(0);
    const isActivatorError = ref(false);

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const stepActivatorProgress = async () => {
      for (const [value, wait] of [
        [0, 200],
        [25, 400],
        [59, 600],
        [75, 0],
      ]) {
        activatorProgress.value = value;
        if (wait) await delay(wait);
      }
    };

    const publishInActivator = async () => {
      isActivatorLoading.value = true;
      activatorProgress.value = 0;
      await stepActivatorProgress();

      try {
        const { url, statusCode } = await getActivatorUrl(route.params.conversationId);
        if (statusCode === 404) {
          isActivatorError.value = true;
        } else if (url) {
          activatorProgress.value = 100;
          await delay(300);
          window.open(url, '_blank');
        }
      } finally {
        await delay(200);
        isActivatorLoading.value = false;
        activatorProgress.value = 0;
      }
    };

    return {
      t,
      publishInActivator,
      isActivatorLoading,
      isActivatorError,
      activatorProgress,
      isDownloadInProgress,
    };
  },
};
</script>

<style lang="scss" scoped>
.dropdown-button {
  :deep(.v-btn__content) {
    padding: 0 $bb-spacing-2 0 $bb-spacing-3;
    height: 100%;
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
    color: $bb-neutral-1;
    gap: $bb-spacing-1;

    i {
      color: #f242ff;
    }

    &:hover {
      background-color: $bb-neutral-5 !important;
    }
  }

  :deep(.v-btn__append) {
    border-left: 1px solid $bb-neutral-4;
    height: 100%;
    margin: 0 !important;
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;

    .bb-icon {
      padding: 0 $bb-spacing-3 0 $bb-spacing-2;
      background-color: inherit !important;
      color: $bb-neutral-1;
    }

    &:hover,
    &:active,
    &:focus {
      background-color: $bb-neutral-5 !important;
    }
  }
}

:deep(.v-list-item__content) {
  display: flex !important;
  align-items: center !important;
  gap: $bb-spacing-1 !important;
}
</style>
